import { ref, nextTick, type Ref } from 'vue';
import { useAddService, useDeleteService, useUpdateService, useDuplicateService } from '@/queries/workflows';
import  { serviceModelTypes} from "@/types/workflow-types";
import { useAlertStore } from '@/stores/alerts-store';
import { defaultServiceModel } from '@/views/builder/utils/defaultServiceModel';

/**
 * Composable for managing node operations in the Builder view
 * This includes creating, updating, deleting, and manipulating workflow nodes
 */
export function useNodeOperations(
  workflowId: Ref<string>,
  nodes: Ref<any[]>,
  edges: Ref<any[]>,
  getViewport: () => { x: number; y: number; zoom: number },
  getPositionKey: () => string,
  nodePositions: Ref<Map<string, { TB: Map<string, { x: number; y: number }>; LR: Map<string, { x: number; y: number }> }>>,
  flowDirection: Ref<"TB" | "LR">,
  // State from useBuilderState
  serviceToRename: Ref<{ id: string; currentName: string } | null>,
  renameInputValue: Ref<string>,
  serviceToDuplicate: Ref<{ id: string; name: string } | null>,
  serviceToDelete: Ref<{ id: string; name: string } | null>,
  serviceToToggle: Ref<{ id: string; name: string; isDisabled: boolean } | null>,
  // Functions from useBuilderState
  openRenameServiceModal: (serviceId: string, currentName: string, mode?: "rename" | "create") => void,
  closeRenameServiceModal: () => void,
  openDuplicateServiceModal: (serviceId: string, serviceName: string) => void,
  closeDuplicateServiceModal: () => void,
  openDeleteServiceModal: (serviceId: string, serviceName: string) => void,
  closeDeleteServiceModal: () => void,
  openToggleServiceModal: (serviceId: string, serviceName: string, isDisabled: boolean) => void,
  closeToggleServiceModal: () => void,
  closeAddServicePanel: () => void,
  // Edit-parents modal (relationship editing) — appended so existing positional args are undisturbed
  serviceToEditParents: Ref<{ id: string; name: string } | null>,
  openEditParentsModal: (serviceId: string, serviceName: string) => void,
  closeEditParentsModal: () => void
) {
  const { addServiceAsync } = useAddService(workflowId);
  const { deleteService } = useDeleteService(workflowId);
  const { updateServiceAsync } = useUpdateService(workflowId);
  const { duplicateServiceAsync } = useDuplicateService(workflowId);
  const alerts = useAlertStore();

  // Persist a just-created/duplicated node's canvas position so it survives the next mutation's
  // refetch (its server-side canvasLayout is otherwise {0,0} = "not placed" and would auto-layout).
  // Best-effort — a failed layout save is not worth surfacing; the node stays put locally.
  async function persistNodeLayout(serviceId: string, position: { x: number; y: number }) {
    try {
      await updateServiceAsync({ serviceId, service: { canvasLayout: { x: position.x, y: position.y } } });
    } catch {
      // ignore — layout is cosmetic; the next successful edit reconciles it
    }
  }

  /**
   * Add a new service node to the workflow
   */
  async function addNewService(serviceType: string, parentNodeId: string | null) {
    // Extract parent service IDs from parent node if it exists
    const parentServiceIds: string[] = [];
    if (parentNodeId) {
      const parentNode = nodes.value.find((n) => n.id === parentNodeId);
      if (parentNode && parentNode.data.serviceId) {
        parentServiceIds.push(parentNode.data.serviceId);
      }
    }

    // Create the service model DTO
    const serviceModelDto = defaultServiceModel(serviceType);

    // Call TQ mutation to add service and get the service ID
    //TODO: allow naming the service
    const name = "New Service";
    const serviceId = await addServiceAsync({
      name,
      serviceType: serviceModelDto,
      parentServiceIds: parentServiceIds.length > 0 ? parentServiceIds : [],
    });

    //TODO: confirm ok to use the backend service ID as the node ID
    // Generate a unique ID for the new node (UI-only ID)
    // const newNodeId = `node-${Date.now()}`;


    // Find parent node to calculate position
    let newPosition = { x: 200, y: 300 }; // Default position

    if (parentNodeId) {
      const parentNode = nodes.value.find((n) => n.id === parentNodeId);
      if (parentNode) {
        // Position new node below parent
        newPosition = {
          x: parentNode.position.x,
          y: parentNode.position.y + 180,
        };
      }
    } else {
      // No parent - standalone node from menu
      if (nodes.value.length === 0) {
        // First node - place at default position
        newPosition = { x: 200, y: 120 };
      } else {
        // Standalone node - position it relative to current viewport center
        const currentViewport = getViewport();
        const existingPositions = nodes.value.map((n) => ({
          x: n.position.x,
          y: n.position.y,
        }));

        // Calculate viewport center in world coordinates
        const viewportCenterX =
          -currentViewport.x / currentViewport.zoom +
          window.innerWidth / 2 / currentViewport.zoom;
        const viewportCenterY =
          -currentViewport.y / currentViewport.zoom +
          window.innerHeight / 2 / currentViewport.zoom;

        // Try to place node near viewport center, but avoid overlap with existing nodes
        let offsetX = viewportCenterX - 120; // Offset to center the node (240px wide / 2)
        let offsetY = viewportCenterY - 60; // Offset to center the node (120px tall / 2)
        let attempts = 0;

        while (attempts < 20) {
          const testPosition = { x: offsetX, y: offsetY };
          const tooClose = existingPositions.some(
            (pos) =>
              Math.abs(pos.x - testPosition.x) < 200 &&
              Math.abs(pos.y - testPosition.y) < 150
          );

          if (!tooClose) {
            newPosition = testPosition;
            break;
          }

          // Try positions in a spiral pattern around the viewport center
          const angle = attempts * 30 * (Math.PI / 180); // 30 degrees per attempt
          const radius = 150 + attempts * 50; // Increasing radius
          offsetX = viewportCenterX + Math.cos(angle) * radius - 120;
          offsetY = viewportCenterY + Math.sin(angle) * radius - 60;
          attempts++;
        }
      }
    }

    // Create new node
    const newNode = {
      id: serviceId,
      type: "service",
      position: newPosition,
      data: {
        serviceId: serviceId, // Store the backend service ID for mapping
        label: name,
        description: "",
        status: "Active",
        version: "v1.0.0",
        processing: false,
        running: false,
        completed: false,
        failed: false,
        disabled: false,
        isFirstNode: nodes.value.length === 0 && !parentNodeId,
      },
    };

    nodes.value.push(newNode);

    // Save position
    const key = getPositionKey();
    const direction = flowDirection.value;
    if (!nodePositions.value.has(key)) {
      nodePositions.value.set(key, { TB: new Map(), LR: new Map() });
    }
    nodePositions.value.get(key)![direction].set(serviceId, { ...newPosition });

    // Persist the placement so the new node survives the next mutation's refetch instead of resetting
    // to auto-layout (its server-side canvasLayout is otherwise {0,0} = "not placed"). Best-effort.
    void persistNodeLayout(serviceId, newPosition);

    // Create edge if there's a parent
    if (parentNodeId) {
      const newEdge = {
        id: `e${parentNodeId}->${serviceId}`,
        source: parentNodeId,
        target: serviceId,
        type: "animated",
      };
      edges.value.push(newEdge);
    }

    // Close the add service panel after adding the service
    closeAddServicePanel();

    // Immediately offer to name the just-created service, reusing the rename modal in "create" mode.
    // Cancelling leaves the default name; the node already exists on the canvas and server.
    openRenameServiceModal(serviceId, name, "create");

    return serviceId;
  }

  /**
   * Rename a service node
   */
  function handleRenameService(nodeId: string) {
    const node = nodes.value.find((n) => n.id === nodeId);
    if (node) {
      openRenameServiceModal(nodeId, node.data.label);
    }
  }

  /**
   * Confirm rename operation. Updates the canvas optimistically, then persists the new name to the
   * server via a partial service update (name only) — without this the rename was lost on reload.
   */
  async function handleConfirmRename() {
    const target = serviceToRename.value;
    const newName = renameInputValue.value.trim();
    if (target && newName) {
      const node = nodes.value.find((n) => n.id === target.id);
      if (node && newName !== target.currentName) {
        node.data.label = newName;
        // Persist against the backend service id, not the node id — a duplicated node's id diverges.
        await updateServiceAsync({
          serviceId: node.data.serviceId,
          service: { name: newName },
        });
      }
    }
    closeRenameServiceModal();
  }

  /**
   * Cancel rename operation
   */
  function handleCancelRename() {
    closeRenameServiceModal();
  }

  /**
   * Duplicate a service node
   */
  function handleDuplicateService(nodeId: string) {
    const nodeToDuplicate = nodes.value.find((node) => node.id === nodeId);
    if (nodeToDuplicate) {
      openDuplicateServiceModal(nodeId, nodeToDuplicate.data.label);
    }
  }

  /**
   * Confirm duplicate operation
   */
  async function confirmDuplicateService() {
    if (serviceToDuplicate.value) {
      const nodeId = serviceToDuplicate.value.id;
      const nodeToDuplicate = nodes.value.find((n) => n.id === nodeId);
      if (!nodeToDuplicate) {
        closeDuplicateServiceModal();
        return;
      }

      // Deep-copy the service server-side; the returned id is the new service's real backend id, used
      // as both the node id and data.serviceId so canvas ops (rename, delete, run) target the copy.
      const duplicateNodeId = await duplicateServiceAsync(nodeToDuplicate.data.serviceId);

      // Position the duplicate node to the right of the original with some spacing
      const duplicatePosition = {
        x: nodeToDuplicate.position.x + 250, // 250px to the right
        y: nodeToDuplicate.position.y,
      };

      // Check for collision with existing nodes and adjust position if needed
      const existingPositions = nodes.value.map((n) => ({
        x: n.position.x,
        y: n.position.y,
      }));
      let attempts = 0;
      let offsetX = duplicatePosition.x;
      let offsetY = duplicatePosition.y;

      while (attempts < 10) {
        const testPosition = { x: offsetX, y: offsetY };
        const tooClose = existingPositions.some(
          (pos) =>
            Math.abs(pos.x - testPosition.x) < 200 &&
            Math.abs(pos.y - testPosition.y) < 100
        );

        if (!tooClose) {
          duplicatePosition.x = offsetX;
          duplicatePosition.y = offsetY;
          break;
        }

        // Try positions in a pattern: right, down-right, down, down-left, etc.
        const angle = attempts * 45 * (Math.PI / 180); // 45 degrees per attempt
        const distance = 250;
        offsetX = nodeToDuplicate.position.x + Math.cos(angle) * distance;
        offsetY = nodeToDuplicate.position.y + Math.sin(angle) * distance;
        attempts++;
      }

      // Create the duplicate node with same display data but the new backend id and position
      const duplicateNode = {
        id: duplicateNodeId,
        type: nodeToDuplicate.type,
        position: duplicatePosition,
        data: {
          ...nodeToDuplicate.data,
          // Point at the new service, not the source (the spread copied the source's id)
          serviceId: duplicateNodeId,
          // Matches the server-generated name from DuplicateService
          label: `${nodeToDuplicate.data.label} (Copy)`,
          // Reset any runtime states
          executionData: undefined,
          // Keep the original service configuration
          subtitle: nodeToDuplicate.data.subtitle,
          disabled: nodeToDuplicate.data.disabled || false,
          isFirstNode: false, // Duplicates should never be first nodes
        },
      };

      // Add the duplicate node to the nodes array
      nodes.value.push(duplicateNode);

      // Persist the duplicate's placement so it survives the next mutation's refetch.
      void persistNodeLayout(duplicateNodeId, duplicatePosition);
    }

    // Close modal and reset state
    closeDuplicateServiceModal();
  }

  /**
   * Cancel duplicate operation
   */
  function cancelDuplicateService() {
    closeDuplicateServiceModal();
  }

  /**
   * Toggle disabled state of a service
   */
  function handleToggleDisabled(nodeId: string) {
    const nodeToToggle = nodes.value.find((node) => node.id === nodeId);
    if (nodeToToggle) {
      openToggleServiceModal(
        nodeId,
        nodeToToggle.data.label,
        !!nodeToToggle.data.disabled
      );
    }
  }

  /**
   * Confirm toggle disabled operation
   */
  function confirmToggleService() {
    if (serviceToToggle.value) {
      const nodeId = serviceToToggle.value.id;
      const node = nodes.value.find((n) => n.id === nodeId);

      if (node) {
        // Toggle the disabled state
        node.data.disabled = !node.data.disabled;
      }
    }

    // Close modal and reset state
    closeToggleServiceModal();
  }

  /**
   * Cancel toggle operation
   */
  function cancelToggleService() {
    closeToggleServiceModal();
  }

  /**
   * Delete a service node
   */
  function handleDeleteService(nodeId: string) {
    const nodeToDelete = nodes.value.find((node) => node.id === nodeId);
    if (nodeToDelete) {
      openDeleteServiceModal(nodeId, nodeToDelete.data.label);
    }
  }

  /**
   * Confirm delete operation
   */
  async function confirmDeleteService() {
    if (serviceToDelete.value) {
      const nodeId = serviceToDelete.value.id;
      const node = nodes.value.find((n) => n.id === nodeId);

      // Optimistic: remove from canvas immediately, TQ confirms via invalidation
      if (node && node.data.serviceId) {
        deleteService(node.data.serviceId);
      }

      // Remove the node from nodes array
      nodes.value = nodes.value.filter((node) => node.id !== nodeId);

      // Remove all edges connected to this node
      edges.value = edges.value.filter(
        (edge) => edge.source !== nodeId && edge.target !== nodeId
      );
    }

    // Close modal and reset state
    closeDeleteServiceModal();
  }

  /**
   * Cancel delete operation
   */
  function cancelDeleteService() {
    closeDeleteServiceModal();
  }

  /**
   * Open the "Edit Parent Services" modal for a node. Targets the backend service id (a duplicated
   * node's id diverges from it), same discipline as rename.
   */
  function handleEditParents(nodeId: string) {
    const node = nodes.value.find((n) => n.id === nodeId);
    if (node) {
      openEditParentsModal(node.data.serviceId ?? nodeId, node.data.label);
    }
  }

  /**
   * Persist the new parent set for the service. Relationships apply immediately (own endpoint with
   * server-side cycle validation), unlike the ServiceEditor's batch dirty-map save. On a server
   * rejection (e.g. a cycle the client didn't exclude) keep the modal open and surface the reason.
   */
  async function confirmEditParents(selectedParentIds: string[]) {
    const target = serviceToEditParents.value;
    if (!target) return;
    try {
      await updateServiceAsync({
        serviceId: target.id,
        service: { parentServiceIds: selectedParentIds },
      });
      closeEditParentsModal();
    } catch (e) {
      const message =
        (e as { userMessage?: string; message?: string })?.userMessage ??
        (e as Error)?.message ??
        "That change would create a cycle.";
      alerts.addError("Couldn't update relationships", message);
    }
  }

  function cancelEditParents() {
    closeEditParentsModal();
  }

  return {
    // Core node operations
    addNewService,
    
    // Rename operations
    handleRenameService,
    handleConfirmRename,
    handleCancelRename,
    
    // Duplicate operations
    handleDuplicateService,
    confirmDuplicateService,
    cancelDuplicateService,
    
    // Toggle operations
    handleToggleDisabled,
    confirmToggleService,
    cancelToggleService,
    
    // Delete operations
    handleDeleteService,
    confirmDeleteService,
    cancelDeleteService,

    // Edit-parents (relationship) operations
    handleEditParents,
    confirmEditParents,
    cancelEditParents,
  };
}